<template>
  <div class="cpq-configurator">
    <Tabs v-model="activeTab">
      <!-- CPQ Mode Selection -->
      <Tab name="mode" title="CPQ Mode">
        <section class="cpq-mode-selector">
          <h3>Kies je CPQ Mode</h3>
          <p class="mode-description">
            Selecteer hoe je CPQ wilt configureren. Je kunt altijd later van mode wisselen.
          </p>

          <div class="mode-options">
            <div
              class="mode-option"
              :class="{ 'mode-option--active': cpqMode === 'feed-based' }"
              @click="setCPQMode('feed-based')"
            >
              <div class="mode-option__icon">
                <BaseIcon name="database" size="32" />
              </div>
              <h4>Feed-Based CPQ</h4>
              <p>
                Gebruik bestaande product feeds met geavanceerde attribute mapping en prijsregels.
              </p>
              <ul class="mode-features">
                <li>✅ Complexe attribute mapping</li>
                <li>✅ Geavanceerde prijsregels</li>
                <li>✅ Feed-based product matching</li>
                <li>✅ Bestaande configuraties</li>
              </ul>
              <Btn
                :variant="cpqMode === 'feed-based' ? 'primary' : 'secondary'"
                @click.stop="setCPQMode('feed-based')"
              >
                {{ cpqMode === 'feed-based' ? 'Actief' : 'Selecteer' }}
              </Btn>
            </div>

            <div
              class="mode-option"
              :class="{ 'mode-option--active': cpqMode === 'product-configurator' }"
              @click="setCPQMode('product-configurator')"
            >
              <div class="mode-option__icon">
                <BaseIcon name="settings" size="32" />
              </div>
              <h4>Product Configurator</h4>
              <p>Automatische formulier generatie uit specifieke producten met hun opties.</p>
              <ul class="mode-features">
                <li>🚀 Automatische form generatie</li>
                <li>🎯 Product-specifieke opties</li>
                <li>💰 Directe product pricing</li>
                <li>⚡ Snelle setup</li>
              </ul>
              <Btn
                :variant="cpqMode === 'product-configurator' ? 'primary' : 'secondary'"
                @click.stop="setCPQMode('product-configurator')"
              >
                {{ cpqMode === 'product-configurator' ? 'Actief' : 'Selecteer' }}
              </Btn>
            </div>
          </div>

          <div v-if="cpqMode" class="mode-status">
            <div class="status-indicator">
              <BaseIcon name="check-circle" size="20" />
              <span>
                <strong>
                  {{ cpqMode === 'feed-based' ? 'Feed-Based CPQ' : 'Product Configurator' }}
                </strong>
                is actief
              </span>
            </div>
          </div>
        </section>
      </Tab>

      <Tab name="config" title="Configuration" :disabled="!cpqMode">
        <section class="cpq-configurator__section">
          <label style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 1rem">
            <input type="checkbox" v-model="cpqEnabled" />
            <span>CPQ enabled</span>
          </label>

          <!-- Show auto-generation options if this is a multi-product CPQ -->
          <div
            v-if="cpqConfig.mode === 'multi-product'"
            style="margin-bottom: 1rem; padding: 1rem; background: #f8f9fa; border-radius: 8px"
          >
            <h4>Multi-Product CPQ Mode</h4>
            <label style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem">
              <input type="checkbox" v-model="autoGenerate" />
              <span>Automatisch formulier genereren op basis van product feeds</span>
            </label>
            <p style="font-size: 0.875rem; color: #6b7280; margin: 0">
              Wanneer ingeschakeld worden formulier pagina's automatisch gegenereerd op basis van de
              geselecteerde product feeds.
            </p>
          </div>
          <h3>Productnaam</h3>
          <TextInput
            id="product-name"
            label="Productnaam"
            :modelValue="productName"
            @update:modelValue="(val) => (productName = val)"
            placeholder="Bijv. Vlaklichtkoepel Daklicht Geïsoleerd"
            style="max-width: 400px; margin-bottom: 1rem"
          />
          <h3>Step 1: Multi-Product Configuration</h3>

          <!-- Multi-product mode toggle -->
          <label style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 1rem">
            <input type="checkbox" v-model="multiProductMode" />
            <span>Multi-product mode (selecteer specifieke producten uit feeds)</span>
          </label>

          <!-- Feed-Based Configuration -->
          <div v-if="cpqMode === 'feed-based' && !multiProductMode">
            <SelectInput
              id="feed-select"
              label="Selected feed"
              :options="feeds.map((feed) => ({ value: feed.id, label: feed.name }))"
              :modelValue="selectedFeedId"
              @update:modelValue="(val) => (selectedFeedId = val)"
            />
          </div>

          <!-- Product Configurator Configuration -->
          <div v-if="cpqMode === 'product-configurator'" class="product-configurator-section">
            <h3>Product Configurator Setup</h3>
            <p class="section-description">
              Selecteer specifieke producten die beschikbaar moeten zijn in deze configurator. Het
              formulier wordt automatisch gegenereerd op basis van de product opties.
            </p>

            <div class="configurator-settings">
              <div class="setting-group">
                <h4>Design Instellingen</h4>
                <div class="settings-grid">
                  <ToggleInput
                    id="show-pricing"
                    label="Toon prijzen tijdens configuratie"
                    v-model="configuratorSettings.showPricing"
                  />
                  <ToggleInput
                    id="show-images"
                    label="Toon product afbeeldingen"
                    v-model="configuratorSettings.showImages"
                  />
                  <ToggleInput
                    id="auto-generate"
                    label="Automatisch formulier genereren"
                    v-model="configuratorSettings.autoGenerate"
                  />
                  <ToggleInput
                    id="enable-upsells"
                    label="Upsells inschakelen"
                    v-model="configuratorSettings.enableUpsells"
                  />
                </div>
              </div>

              <div class="setting-group">
                <h4>Geselecteerde Producten</h4>
                <div v-if="!configuratorProducts.length" class="no-products">
                  <p>Nog geen producten geselecteerd voor deze configurator.</p>
                  <Btn @click="openProductSelector" variant="secondary" icon="plus">
                    Producten Selecteren
                  </Btn>
                </div>

                <div v-else class="selected-products-overview">
                  <div class="products-summary">
                    <strong>{{ configuratorProducts.length }} product(en) geselecteerd</strong>
                  </div>

                  <div class="products-list">
                    <div
                      v-for="product in configuratorProducts"
                      :key="product.id"
                      class="product-item"
                    >
                      <img v-if="product.imageUrl" :src="product.imageUrl" :alt="product.name" />
                      <div class="product-info">
                        <h5>{{ product.name }}</h5>
                        <p v-if="product.description" class="product-info__description">
                          {{ product.description }}
                        </p>
                        <span class="product-options">
                          {{ product.options?.length || 0 }} opties
                        </span>
                      </div>
                      <Btn
                        @click="removeConfiguratorProduct(product.id)"
                        variant="plain"
                        icon="xmark"
                        size="sm"
                      />
                    </div>
                  </div>

                  <div class="products-actions">
                    <Btn @click="openProductSelector" variant="secondary" icon="plus">
                      Meer Producten
                    </Btn>
                    <Btn @click="regenerateConfiguratorForm" variant="primary" icon="refresh">
                      Formulier Regenereren
                    </Btn>
                  </div>
                </div>
              </div>
            </div>

            <!-- Extra Velden -->
            <div class="setting-group">
              <h4>Extra velden</h4>
              <p class="section-description">
                Voeg algemene velden toe die na de productconfiguratie worden getoond, zoals
                opmerkingen of situatiefoto's. De waarden worden automatisch gekoppeld aan het
                project.
              </p>

              <div v-if="extraFields.length" class="extra-fields-list">
                <div
                  v-for="(field, idx) in extraFields"
                  :key="field.id"
                  class="extra-field-item"
                >
                  <div class="extra-field-item__row">
                    <SelectInput
                      :id="`extra-field-type-${idx}`"
                      label="Type"
                      v-model="field.type"
                      :options="extraFieldTypeOptions"
                      class="extra-field-item__type"
                    />
                    <TextInput
                      :id="`extra-field-label-${idx}`"
                      label="Label"
                      v-model="field.label"
                      placeholder="bijv. Opmerkingen voor de monteur"
                      class="extra-field-item__label"
                    />
                    <SelectInput
                      :id="`extra-field-map-${idx}`"
                      label="Koppel aan project"
                      v-model="field.mapToProject"
                      :options="mapToProjectOptions"
                      class="extra-field-item__map"
                    />
                    <div class="extra-field-item__actions">
                      <ToggleInput
                        :id="`extra-field-required-${idx}`"
                        label="Verplicht"
                        v-model="field.required"
                      />
                      <Btn
                        variant="plain"
                        size="sm"
                        @click="removeExtraField(idx)"
                        type="button"
                      >
                        <BaseIcon name="xmark" size="14" />
                      </Btn>
                    </div>
                  </div>
                  <div
                    v-if="
                      (field.type === 'file-upload' || field.type === 'photo-drawing') &&
                      field.mapToProject === 'situation_photos'
                    "
                    class="extra-field-item__photo-note"
                  >
                    {{
                      field.type === 'photo-drawing'
                        ? "Tekeningen worden als situatiefoto's gekoppeld aan het project."
                        : "Foto's worden automatisch als situatiefoto's gekoppeld aan het project."
                    }}
                  </div>
                  <TextInput
                    v-if="field.type === 'file-upload' || field.type === 'photo-drawing'"
                    :id="`extra-field-instruction-${idx}`"
                    label="Instructie voor de klant"
                    v-model="field.instruction"
                    :placeholder="
                      field.type === 'photo-drawing'
                        ? 'bijv. Maak een foto en teken de afmetingen erop...'
                        : 'bijv. Maak een foto van de huidige situatie...'
                    "
                  />
                </div>
              </div>

              <Btn variant="dashed" icon="plus" @click="addExtraField" type="button">
                Veld toevoegen
              </Btn>
            </div>
          </div>

          <!-- New multi-product selection -->
          <div v-if="multiProductMode" class="multi-product-section">
            <h4>Selecteer producten voor deze configurator</h4>

            <!-- Feed browser -->
            <div class="feed-browser">
              <div v-for="feed in feeds" :key="feed.id" class="feed-section">
                <h5>{{ feed.name }}</h5>
                <div class="products-grid">
                  <div
                    v-for="product in feed.products"
                    :key="product.title"
                    class="product-card"
                    :class="{ selected: isProductSelected(feed.id, getProductId(product)) }"
                    @click="toggleProduct(feed.id, getProductId(product), product)"
                  >
                    <img v-if="product.imageUrl" :src="product.imageUrl" :alt="product.title" />
                    <div class="product-info">
                      <h6>{{ product.title }}</h6>
                      <p v-if="product.description" class="product-info__description">
                        {{ product.description }}
                      </p>
                      <span v-if="product.price" class="price">€{{ product.price }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Selected products overview -->
            <div v-if="selectedProducts.length" class="selected-products">
              <h4>Geselecteerde producten ({{ selectedProducts.length }})</h4>
              <div class="selected-products-list">
                <div
                  v-for="item in selectedProducts"
                  :key="`${item.feedId}-${item.productId}`"
                  class="selected-product"
                >
                  <span>{{ item.product.title }}</span>
                  <span class="feed-name">({{ getFeedName(item.feedId) }})</span>
                  <button @click="removeProduct(item.feedId, item.productId)" class="remove-btn">
                    ×
                  </button>
                </div>
              </div>
            </div>

            <!-- Generate form button -->
            <div v-if="selectedProducts.length" class="generate-section">
              <button @click="generateMultiProductForm" class="btn btn--primary">
                Genereer formulier voor {{ selectedProducts.length }} producten
              </button>
            </div>
          </div>
        </section>
        <section class="cpq-configurator__section" v-if="selectedFeed">
          <h3>Step 2: Attributes</h3>
          <ul>
            <li v-for="attr in selectedFeed.attributes" :key="attr.key">
              <strong>{{ attr.label }}</strong>
              ({{ attr.key }}) - {{ attr.type }}
            </li>
          </ul>
        </section>
        <section class="cpq-configurator__section" v-if="selectedFeed">
          <h3>Step 3: Attribute Mapping</h3>
          <div
            v-for="(mapping, idx) in attributeMapping"
            :key="idx"
            style="display: flex; gap: 1rem; align-items: center; margin-bottom: 0.5rem"
          >
            <select v-model="mapping.attribute" style="min-width: 120px">
              <option value="">Select attribute</option>
              <option v-for="attr in selectedFeed.attributes" :key="attr.key" :value="attr.key">
                {{ attr.label }}
              </option>
            </select>
            <select v-model="mapping.questionId" style="min-width: 120px">
              <option value="">Select question</option>
              <option v-for="q in formQuestions" :key="q.id" :value="q.id">{{ q.label }}</option>
            </select>
            <Btn icon="minus" variant="tertiary" @click="attributeMapping.splice(idx, 1)" />
          </div>
          <Btn
            icon="plus-circle"
            variant="tertiary"
            @click="attributeMapping.push({ attribute: '', questionId: '' })"
          >
            Add mapping
          </Btn>
        </section>
      </Tab>
      <Tab name="pricing" title="Pricing rules & Preview">
        <section class="cpq-configurator__section" v-if="selectedFeed">
          <NumberInput
            id="base-price"
            label="Base price (€)"
            :modelValue="basePrice"
            @update:modelValue="(val) => (basePrice = Number(val))"
            :min="0"
          />
          <CPQPriceRulesEditor
            v-model="priceRules"
            :attributes="selectedFeed.attributes"
            :basePrice="basePrice"
            :previewValues="previewValues"
          />
        </section>
        <section class="cpq-configurator__section" v-if="selectedFeed">
          <h3>5. Offerte preview</h3>
          <div class="cpq-offerte-preview">
            <div><strong>Gekozen opties:</strong></div>
            <ul>
              <li v-for="attr in selectedFeed.attributes" :key="attr.key">
                {{ attr.label }}: {{ previewValues[attr.key] || '-' }}
              </li>
            </ul>
          </div>
        </section>
      </Tab>
    </Tabs>
    <div style="margin-top: 2rem; text-align: right">
      <Btn :loading="saving" variant="primary" icon="save" @click="saveCPQConfig">Save</Btn>
      <span v-if="saveSuccess" style="color: var(--green-600); margin-left: 1rem; font-weight: 500">
        Saved!
      </span>
    </div>
  </div>
</template>

<script>
  import { ref, computed, watch, onMounted } from 'vue';
  import { uid } from 'uid';
  import { getProductFeeds } from '@shared/services/productService';
  import CPQPriceRulesEditor from './CPQPriceRulesEditor.vue';
  import { useFormStore } from '@/store/formStore';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import NumberInput from '@/components/inputs/NumberInput.vue';
  import ToggleInput from '@/components/inputs/ToggleInput.vue';
  import Tabs from '@/components/partials/Tabs/Tabs.vue';
  import Tab from '@/components/partials/Tabs/Tab.vue';
  import Btn from '@/components/partials/Btn.vue';
  import TextInput from '@/components/inputs/TextInput.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';

  // Default CPQ configuration
  const defaultCPQConfig = {
    enabled: false,
    selectedFeed: '',
    attributeMapping: [],
    basePrice: 1000,
    priceRules: [],
    productName: '',
  };

  function getCPQConfig(settings) {
    const cpqSettings = settings?.cpq || {};
    return {
      ...defaultCPQConfig,
      ...cpqSettings,
      // Ensure priceRules is always an array
      priceRules: Array.isArray(cpqSettings.priceRules) ? cpqSettings.priceRules : [],
    };
  }

  export default {
    name: 'CPQConfigurator',
    components: {
      CPQPriceRulesEditor,
      SelectInput,
      NumberInput,
      ToggleInput,
      Tabs,
      Tab,
      Btn,
      TextInput,
      BaseIcon,
    },
    props: {
      form: { type: Object, required: true },
    },
    setup(props) {
      const activeTab = ref('config');
      const feeds = ref([]);
      const formStore = useFormStore();
      // Laad cpq-config met fallback
      const cpqConfig = ref(getCPQConfig(formStore.currentForm.settings));

      const cpqMode = ref(cpqConfig.value.mode || '');
      const setCPQMode = (mode) => {
        cpqMode.value = mode;
      };

      const cpqEnabled = ref(cpqConfig.value.enabled);
      const selectedFeedId = ref(cpqConfig.value.selectedFeed);
      const attributeMapping = ref(JSON.parse(JSON.stringify(cpqConfig.value.attributeMapping)));
      const basePrice = ref(cpqConfig.value.basePrice);
      const priceRules = ref(
        cpqConfig.value.priceRules.length > 0
          ? JSON.parse(JSON.stringify(cpqConfig.value.priceRules))
          : [
              {
                id: 'r1',
                label: 'Breedte > 100 & HR++',
                showInQuote: true,
                rootGroup: {
                  logic: 'AND',
                  rules: [{ attribute: null, operator: '==', value: '' }],
                  subGroups: [],
                },
                actions: [{ type: 'add', value: 100 }],
              },
            ]
      );
      const productName = ref(cpqConfig.value.productName || '');

      const configuratorSettings = ref({
        showPricing: cpqConfig.value.showPricing ?? true,
        showImages: cpqConfig.value.showImages ?? true,
        autoGenerate: cpqConfig.value.autoGenerate ?? false,
        enableUpsells: cpqConfig.value.enableUpsells ?? false,
      });

      // Product-configurator mode: product list is managed via ConfiguratorManagementPage
      const configuratorProducts = ref([]);
      const openProductSelector = () => {};
      const removeConfiguratorProduct = () => {};
      const regenerateConfiguratorForm = () => {};

      // Extra fields state
      const extraFields = ref(
        JSON.parse(JSON.stringify(cpqConfig.value.extraFields || []))
      );

      const extraFieldTypeOptions = [
        { value: 'short-answer', label: 'Kort antwoord' },
        { value: 'long-answer', label: 'Lang antwoord' },
        { value: 'file-upload', label: 'Foto upload' },
        { value: 'photo-drawing', label: 'Foto met tekening' },
      ];

      const mapToProjectOptions = [
        { value: '', label: '(Niet koppelen)' },
        { value: 'customer_notes', label: 'Klantopmerkingen (project)' },
        { value: 'internal_notes', label: 'Interne notities (project)' },
        { value: 'situation_photos', label: "Situatiefoto's (project)" },
      ];

      const addExtraField = () => {
        extraFields.value.push({
          id: uid(),
          type: 'short-answer',
          label: '',
          required: false,
          mapToProject: '',
          instruction: '',
        });
      };

      const removeExtraField = (idx) => {
        extraFields.value.splice(idx, 1);
      };

      // Multi-product state
      const multiProductMode = ref(cpqConfig.value.selectedProducts?.length > 0 || false);
      const selectedProducts = ref(cpqConfig.value.selectedProducts || []);

      const isProductSelected = (feedId, productId) =>
        selectedProducts.value.some((p) => p.feedId === feedId && p.productId === productId);
      const getProductId = (product) => product.id || product.title;
      const toggleProduct = (feedId, productId, product) => {
        const idx = selectedProducts.value.findIndex(
          (p) => p.feedId === feedId && p.productId === productId
        );
        if (idx >= 0) {
          selectedProducts.value.splice(idx, 1);
        } else {
          selectedProducts.value.push({ feedId, productId, product });
        }
      };
      const generateMultiProductForm = () => {};
      const getFeedName = (feedId) => feeds.value.find((f) => f.id === feedId)?.name || feedId;

      const selectedFeed = computed(() => feeds.value.find((f) => f.id === selectedFeedId.value));
      const formQuestions = computed(() => {
        const form = formStore.currentForm;
        if (!form?.pages) return [];
        const questionTypes = [
          'single-choice',
          'multiple-choice',
          'picture-choice',
          'input',
          'number',
          'dropdown',
          'date',
          'email',
          'slider',
        ];
        return form.pages.flatMap((page) =>
          page.elements
            .filter((el) => questionTypes.includes(el.type))
            .map((el) => ({
              id: el.id,
              label: el.props?.label || el.label || el.id,
              type: el.type,
            }))
        );
      });
      // Placeholder: preview values (in praktijk: formData)
      const previewValues = ref({});
      const saving = ref(false);
      const saveSuccess = ref(false);
      async function saveCPQConfig() {
        saving.value = true;
        try {
          await formStore.updateCPQSettings(formStore.currentForm.id, {
            enabled: cpqEnabled.value,
            mode: cpqMode.value,
            selectedFeed: selectedFeedId.value,
            attributeMapping: JSON.parse(JSON.stringify(attributeMapping.value)),
            basePrice: basePrice.value,
            priceRules: JSON.parse(JSON.stringify(priceRules.value)),
            productName: productName.value,
            extraFields: JSON.parse(JSON.stringify(extraFields.value)),
            showPricing: configuratorSettings.value.showPricing,
            showImages: configuratorSettings.value.showImages,
            autoGenerate: configuratorSettings.value.autoGenerate,
            enableUpsells: configuratorSettings.value.enableUpsells,
          });
          saveSuccess.value = true;
          setTimeout(() => (saveSuccess.value = false), 2000);
        } catch (e) {
          alert('Fout bij opslaan van CPQ-configuratie');
        } finally {
          saving.value = false;
        }
      }
      onMounted(async () => {
        feeds.value = await getProductFeeds();
      });
      return {
        activeTab,
        feeds,
        cpqMode,
        setCPQMode,
        selectedFeedId,
        selectedFeed,
        cpqEnabled,
        attributeMapping,
        basePrice,
        priceRules,
        formQuestions,
        previewValues,
        saving,
        saveSuccess,
        saveCPQConfig,
        productName,
        configuratorSettings,
        configuratorProducts,
        openProductSelector,
        removeConfiguratorProduct,
        regenerateConfiguratorForm,
        extraFields,
        extraFieldTypeOptions,
        mapToProjectOptions,
        addExtraField,
        removeExtraField,
        multiProductMode,
        selectedProducts,
        isProductSelected,
        getProductId,
        toggleProduct,
        generateMultiProductForm,
        getFeedName,
      };
    },
  };
</script>

<style scoped lang="scss">
  @import '@/assets/scss/base/_mixins.scss';
  .cpq-configurator__section {
    padding: 2rem 0;
    border-bottom: 1px solid var(--slate-200);
    h3 {
      margin-bottom: 1rem;
    }
  }

  .extra-fields-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-bottom: 1rem;
  }

  .extra-field-item {
    padding: 0.75rem;
    border: 1px solid var(--slate-200);
    border-radius: 8px;
    background: var(--slate-50);

    &__row {
      display: grid;
      grid-template-columns: 160px 1fr 220px auto;
      gap: 0.75rem;
      align-items: flex-end;
    }

    &__actions {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding-bottom: 0.25rem;
    }

    &__photo-note {
      font-size: 0.8125rem;
      color: var(--brand-700);
      background: var(--brand-50);
      border: 1px solid var(--brand-200);
      border-radius: 0.375rem;
      padding: 0.375rem 0.625rem;
      margin-top: 0.5rem;
      line-height: 1.4;
    }
  }

  .cpq-configurator__matching-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    @include fontSize('md');

    &__header {
      font-weight: 700;
      padding: 0.5rem;
      border-bottom: 1px solid var(--slate-300);
      text-align: left;
      @include fontSize('md');
    }

    &__cell {
      padding: 0.5rem;
      border-bottom: 1px solid var(--slate-300);
      vertical-align: middle;
    }
    &__select {
      .form-group {
        padding: 0;
      }
    }

    // tr:last-child .cpq-configurator__matching-table__cell {
    //   border-bottom: none;
    // }

    .cpq-configurator__matching-table__cell:last-child,
    .cpq-configurator__matching-table__header:last-child {
      border-right: none;
    }

    .cpq-configurator__matching-table__select,
    .sf-input--select {
      width: 100%;
    }

    select {
      width: 100%;
      padding: 0.75rem 1rem;
      border-radius: 8px;
      border: 1px solid #e5e7eb;
      background: #fafbfc;
      font-size: 1rem;
    }
  }
</style>
